import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useEffect} from 'react';
import conversationsFetcher from '@/api/fetchers/creators/conversations';
import creatorsUpdaters from '@/api/updaters/creators';

export const COLLABORATION_CONVERSATION_QUERY_KEY = 'collaborationConversation';

type UseCollaborationConversationProps = {
  creatorId: string;
  brandId: string;
  enabled: boolean;
};

function useCollaborationConversation({
  creatorId,
  brandId,
  enabled,
}: UseCollaborationConversationProps): {
  conversationId: string | null;
  isLoading: boolean;
} {
  const queryClient = useQueryClient();

  const {data: lookedUpConversation, isFetched: isLookupDone} = useQuery({
    queryKey: [COLLABORATION_CONVERSATION_QUERY_KEY, creatorId, brandId],
    queryFn: () =>
      conversationsFetcher.currentBrandCollaborationConversation({
        queryKey: [],
        meta: {creatorId, brandId},
      }),
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  const createConversation = useMutation({
    mutationFn: () =>
      creatorsUpdaters.createCollaborationConversation({creatorId, brandId}),
    onSuccess: (data) => {
      queryClient.setQueryData(
        [COLLABORATION_CONVERSATION_QUERY_KEY, creatorId, brandId],
        data
      );
    },
  });

  // Reset mutation state when switching to a different creator/brand so that
  // stale `createConversation.data` from a previous run doesn't satisfy the
  // "conversation already exists" guard in the effect below, which would
  // prevent a new conversation from being created for the incoming creator.
  useEffect(() => {
    createConversation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creatorId, brandId]);

  useEffect(() => {
    if (
      isLookupDone &&
      !lookedUpConversation?.id &&
      !createConversation.isLoading &&
      !createConversation.data
    ) {
      createConversation.mutate();
    }
  }, [
    isLookupDone,
    lookedUpConversation?.id,
    createConversation.isLoading,
    createConversation.data,
  ]);

  const conversationId =
    lookedUpConversation?.id ?? createConversation.data?.id ?? null;
  const isLoading = !isLookupDone || createConversation.isLoading;

  return {conversationId, isLoading};
}

export default useCollaborationConversation;
